<template>
    <tr
        :data-message-id="message.id"
        :data-message-groupid="message.groupId"
        :class="rowClass"
        @click="$emit('select', message)"
    >
        <td v-for="header in headers" :key="header.key">
            <template v-if="header.key === 'data-table-expand'" />
            <template v-else-if="header.key === 'status'">
                <v-tooltip bottom>
                    <template #activator="{ props: itemProps }">
                        <v-icon
                            :color="statusColor"
                            :icon="statusIcon"
                            v-bind="itemProps"
                            size="small"
                        />
                    </template>
                    <span>{{ statusTooltip }}</span>
                </v-tooltip>
            </template>
            <template v-else>{{ cellValue(header.key) }}</template>
        </td>
    </tr>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { MessageListItem, MessageStatus } from '../messagesModels';
import { DateToUTC, ToLocalDate } from '@/shared/DateFormatHelpers';

const props = defineProps<{
    message: MessageListItem;
    headers: { key: string }[];
    selected: boolean;
}>();
defineEmits<{ (e: 'select', message: MessageListItem): void }>();

const { t } = useI18n();
const currentDate = computed(() => DateToUTC(new Date()));

const isActive = computed(() => {
    if (!props.message.startPublish || !props.message.endPublish) return false;
    const start = DateToUTC(props.message.startPublish);
    const end = DateToUTC(props.message.endPublish);
    return start < currentDate.value && currentDate.value < end && props.message.status === MessageStatus.Published;
});

const isExpired = computed(() => currentDate.value > DateToUTC(props.message.endPublish));

const rowClass = computed(() => {
    const c: string[] = [];
    if (props.selected) c.push('selected-message');
    if (props.message.status !== MessageStatus.Published) c.push('unpublished-message');
    else if (isActive.value) c.push('active-message');
    else if (isExpired.value) c.push('expired-message');
    else c.push('future-message');
    return c.join(' ');
});

const statusColor = computed(() => {
    if (props.message.status === MessageStatus.Published) return 'primary';
    if (props.message.status === MessageStatus.Unpublished) return 'red-darken-4';
    return 'grey';
});

const statusIcon = computed(() => {
    if (props.message.status === MessageStatus.Published) return 'mdi-check-circle';
    if (props.message.status === MessageStatus.Unpublished) return 'mdi-close-circle';
    return 'mdi-circle';
});

const statusTooltip = computed(() => {
    if (props.message.status === MessageStatus.Published) return t('messages.resources.published');
    if (props.message.status === MessageStatus.Unpublished) return t('messages.resources.unpublished');
    return t('messages.resources.draft');
});

function cellValue(key: string): string {
    const m = props.message;
    switch (key) {
        case 'category': return m.categoryNameNo;
        case 'reason': return m.reasonNo;
        case 'lineName': return m.lineName ?? '';
        case 'departureTime': return ToLocalDate(m.departureTime, 'time') || '';
        case 'departure': return m.departure ?? '';
        case 'scope': return m.sentTo ?? '';
        case 'validFrom': return `${ToLocalDate(m.validFrom, 'date')} - ${ToLocalDate(m.validFrom, 'time')}`;
        case 'validTo': return `${ToLocalDate(m.validTo, 'date')} - ${ToLocalDate(m.validTo, 'time')}`;
        case 'author': return m.author ?? '';
        case 'driverId': return m.driverId ?? '';
        default: return '';
    }
}
</script>
